import { CalendarDays, Clock } from 'lucide-react';

import {
  Access,
  dayMonth,
  openRenewDialog,
  useAccess
} from '@/components/access/useAccess';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

/**
 * How long this account runs, shown only in the last 20 days and never for an
 * open-ended account. Quiet gray until the last week, then amber with the
 * count. Nothing ever opens by itself.
 *
 * Two placements, one wording:
 *  - from `sm` up, a pill in the header beside "Data through", opening a
 *    popover (not a tooltip: a tooltip never opens on a touch screen);
 *  - on a phone, a thin line under the header. The header row is already full
 *    there -- the section tabs scroll inside it -- and one more pill pushed
 *    "Dashboard" out of sight, so the phone gets its own line instead.
 *
 * The words never say "demo" or "trial": the same date is a paying
 * customer's renewal date.
 */

interface Copy {
  label: string;
  title: string;
  body: string;
  soon: boolean;
}

function copyFor(access: Access | null): Copy | null {
  if (!access?.show || !access.last_day || access.days_left == null)
    return null;
  const until = dayMonth(access.last_day);
  if (access.days_left <= 1)
    return {
      label: 'Access ends today',
      title: 'Your access ends today',
      body: `Signals stays open until midnight, ${until}. To keep using it, request a renewal.`,
      soon: true
    };
  if (access.soon)
    return {
      label: `Renewal in ${access.days_left} days`,
      title: 'Your subscription renews soon',
      body: `Your access runs until ${until}. To keep it going after that, request a renewal.`,
      soon: true
    };
  return {
    label: `Access until ${until}`,
    title: `Your access runs until ${until}`,
    body: `To keep it going after ${until}, request a renewal.`,
    soon: false
  };
}

const tone = (soon: boolean) => ({
  background: soon ? 'var(--sig-warn-bg)' : 'var(--sig-panel-deep)',
  border: `1px solid ${soon ? 'var(--sig-warn-border)' : 'var(--sig-border)'}`,
  color: soon ? 'var(--sig-warn-text)' : 'var(--sig-muted)'
});

export default function AccessPill() {
  const { access } = useAccess();
  const copy = copyFor(access);
  if (!access || !copy) return null;
  const Icon = copy.soon ? Clock : CalendarDays;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          dir="ltr"
          className="hidden sm:flex items-center gap-1.5 text-[11.5px] rounded-full px-[9px] py-1 whitespace-nowrap shrink-0"
          style={{ ...tone(copy.soon), fontFamily: 'Inter, sans-serif' }}
        >
          <Icon
            className="size-3"
            style={{ color: copy.soon ? 'var(--sig-warn-icon)' : undefined }}
          />
          {copy.label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(300px,calc(100vw-24px))] p-3.5 text-[13px] bg-[var(--sig-surface)] text-[var(--sig-ink)]"
      >
        <div className="font-medium mb-1">{copy.title}</div>
        <div className="text-[var(--sig-muted)] leading-relaxed">
          {copy.body}
        </div>
        <button
          type="button"
          onClick={openRenewDialog}
          className="mt-3 inline-block rounded-lg border border-[var(--sig-border-strong)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--sig-ink)] hover:bg-[var(--sig-panel)]"
        >
          {access.renewal_requested_at ? 'Request sent' : 'Request renewal'}
        </button>
      </PopoverContent>
    </Popover>
  );
}

/** The phone placement: one line, the date or count and a renew link. */
export function AccessStrip() {
  const { access } = useAccess();
  const copy = copyFor(access);
  if (!access || !copy) return null;
  const Icon = copy.soon ? Clock : CalendarDays;
  const t = tone(copy.soon);

  return (
    <div
      dir="ltr"
      className="sm:hidden flex items-center justify-between gap-2 mx-3 mb-1 rounded-lg px-3 py-1.5 text-[12px]"
      style={{ ...t, fontFamily: 'Inter, sans-serif' }}
    >
      <span className="flex items-center gap-1.5 min-w-0 truncate">
        <Icon
          className="size-3.5 shrink-0"
          style={{ color: copy.soon ? 'var(--sig-warn-icon)' : undefined }}
        />
        {copy.label}
      </span>
      <button
        type="button"
        onClick={openRenewDialog}
        className="shrink-0 font-medium underline underline-offset-2"
      >
        {access.renewal_requested_at ? 'Request sent' : 'Renew'}
      </button>
    </div>
  );
}
